import { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  type ImageSourcePropType,
} from "react-native";
import {
  ShieldCheck,
  TrendingUp,
  Sparkles,
  ChevronRight,
  Info,
  type LucideIcon,
} from "lucide-react-native";
import type {
  Organization,
  ProviderServiceCapability,
} from "@louise/providers/providers.types";
import {
  resolveClinicServices,
  resolveClinicCapabilities,
  type CapabilityEvidence,
  type ResolvedClinicService,
  type ClinicServiceKey,
} from "@louise/clinics/clinicCapabilities";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  getBlocksByKind,
  makeId,
  type ContentBlock,
} from "@src/packages/content";
import { MetaText, SubsectionLabel } from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import ProcedureDetailSheet from "../ProcedureDetailSheet";
import TherapyDetailSheet from "../TherapyDetailSheet";
import TestDetailSheet from "../TestDetailSheet";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Props = { org: Organization };

/**
 * Content blocks that back a chip's "tap for detail" sheet. Keyed by the
 * treatment name (from the provider data) and by the capability catalog key.
 * Only entries that map to an authored `procedure` content block become
 * tappable; everything else stays a plain, non-interactive chip until content
 * exists for it.
 */
const TREATMENT_CONTENT: Record<string, string> = {
  "IVF & ICSI": "proc-fertilization-icsi",
  IUI: "proc-iui",
  "Egg Freezing": "proc-cryopreservation",
  "Embryo Freezing": "proc-cryopreservation",
  "Sperm Freezing": "proc-sperm-freeze",
  "Fertility preservation": "proc-cryopreservation",
  "Ovulation Induction": "proc-ovarian-stimulation",
  PGT: "proc-embryo-biopsy-pgt",
};

/** Fact sheets for the U.S. service cards. Keep this keyed by the resolver's
 * stable service key so a translated label never breaks the link. */
const SERVICE_CONTENT: Partial<Record<ClinicServiceKey, string>> = {
  ivf: "proc-fertilization-icsi",
  iui: "proc-iui",
  "mini-ivf": "proc-mini-ivf",
  "ovulation-induction": "proc-ovarian-stimulation",
  "egg-freezing": "proc-cryopreservation",
  "embryo-freezing": "proc-cryopreservation",
  "sperm-freezing": "proc-sperm-freeze",
  "fertility-preservation": "proc-cryopreservation",
  "pgt-family": "test-preimplantation-genetic-testing",
};

// Capability techniques can map to procedure, test, or legacy add-on content.
const CAPABILITY_CONTENT: Record<string, string> = {
  pgt: "test-preimplantation-genetic-testing",
  "assisted-hatching": "therapy-assisted-hatching",
  "time-lapse": "therapy-time-lapse",
  era: "test-endometrial-receptivity-testing-products-era",
  embryoglue: "therapy-embryo-glue",
  immunology: "therapy-reproductive-immunology",
  prp: "therapy-prp-ovarian",
};

function GroupLabel({ children }: { children: string }) {
  return (
    <SubsectionLabel style={{ marginBottom: 10 }}>{children}</SubsectionLabel>
  );
}

/** A plain pill for a treatment the clinic offers. Tappable (chevron shown)
 *  when there is a detail sheet for it. */
function TreatmentChip({
  label,
  onPress,
}: {
  label: string;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("ClinicTreatmentChip");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label), "clinic_treatment_chip");
        onPress?.();
      }}
      disabled={!onPress}
      style={({ pressed }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          backgroundColor: "rgba(255,255,255,0.25)",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.4)",
          borderRadius: 10,
          paddingHorizontal: 10,
          paddingVertical: 7,
        },
        pressed && { opacity: 0.6 },
      ]}
    >
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.text }}
      >
        {label}
      </Text>
      {onPress ? (
        <ChevronRight
          size={12}
          color={EMBER.colors.textLight}
          strokeWidth={2}
        />
      ) : null}
    </Pressable>
  );
}

/** Distinct icon per evidence strength, mirroring the therapies/access page
 *  (strong = ShieldCheck, moderate = TrendingUp, emerging = Sparkles). */
const EVIDENCE_ICON: Record<CapabilityEvidence, LucideIcon> = {
  strong: ShieldCheck,
  moderate: TrendingUp,
  emerging: Sparkles,
};

/** A capability pill. We only render offered capabilities (always the warm
 *  confirmed state). The leading icon reflects the technique's evidence
 *  strength; a trailing chevron appears when a detail sheet exists. */
function CapabilityChip({
  label,
  evidence,
  onPress,
}: {
  label: string;
  evidence: CapabilityEvidence;
  onPress?: () => void;
}) {
  const Icon = EVIDENCE_ICON[evidence];
  const capturePress = usePressCapture("ClinicCapabilityChip");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label), "clinic_capability_chip");
        onPress?.();
      }}
      disabled={!onPress}
      style={({ pressed }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 5,
          borderRadius: 10,
          paddingHorizontal: 10,
          paddingVertical: 7,
          backgroundColor: `${EMBER.colors.warm}14`,
          borderWidth: 1,
          borderColor: `${EMBER.colors.warm}30`,
        },
        pressed && { opacity: 0.6 },
      ]}
    >
      <Icon size={12} color={EMBER.colors.warm} strokeWidth={2} />
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, fontWeight: "600", color: EMBER.colors.warm }}
      >
        {label}
      </Text>
      {onPress ? (
        <ChevronRight size={12} color={EMBER.colors.warm} strokeWidth={2} />
      ) : null}
    </Pressable>
  );
}

/** Shown when we have nothing confirmed for a capability group. We keep the
 *  group visible and say so, rather than listing every item as grayed out. */
function NoInfo({ label, t }: { label: string; t: TranslationFunction }) {
  return (
    <MetaText style={{ marginTop: 0 }}>
      {t("access.capabilities.no_info", {
        params: { label: label.toLowerCase() },
      })}
    </MetaText>
  );
}

function translateServiceText(
  t: TranslationFunction,
  section: "group" | "item",
  key: string,
  fallback: string,
) {
  const path = `access.clinic_services.${section}.${key}`;
  const translated = t(path);
  return translated === path ? fallback : translated;
}

function getDevServiceOverrides(): ProviderServiceCapability[] | undefined {
  if (!__DEV__ || typeof window === "undefined") return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("dev_clinic_services") !== "states") return undefined;
    const fixtureSource = {
      url: "https://springfertility.com/san-francisco/",
      fetchedAt: "2026-03-11",
    };
    return [
      { key: "pgt-a", status: "offered", _source: fixtureSource },
      { key: "pgt-m", status: "unknown" },
      { key: "pgt-sr", status: "not-offered", _source: fixtureSource },
      { key: "pgt-p", status: "unknown" },
      { key: "monitoring", status: "not-offered", _source: fixtureSource },
    ];
  } catch {
    return undefined;
  }
}

const SERVICE_ILLUSTRATIONS: Record<ClinicServiceKey, ImageSourcePropType> = {
  ivf: require("@src/apps/moon/assets/images/steps/fertilization.png"),
  iui: require("@src/apps/moon/assets/images/steps/insemination.png"),
  "mini-ivf": require("@src/apps/moon/assets/images/clinic-services/mini-ivf.png"),
  "ovulation-induction": require("@src/apps/moon/assets/images/steps/pre_treatment.png"),
  "egg-freezing": require("@src/apps/moon/assets/images/clinic-services/egg-freezing.png"),
  "embryo-freezing": require("@src/apps/moon/assets/images/clinic-services/embryo-freezing.png"),
  "sperm-freezing": require("@src/apps/moon/assets/images/clinic-services/sperm-freezing.png"),
  "fertility-preservation": require("@src/apps/moon/assets/images/steps/freezing.png"),
  "onsite-lab": require("@src/apps/moon/assets/images/steps/analysis.png"),
  "fertility-testing": require("@src/apps/moon/assets/images/steps/screening.png"),
  "pgt-family": require("@src/apps/moon/assets/images/steps/pgt_testing.png"),
  "donor-programs": require("@src/apps/moon/assets/images/clinic-services/donor-programs.png"),
  "gestational-carrier": require("@src/apps/moon/assets/images/clinic-services/gestational-carrier.png"),
  "lgbtq-care": require("@src/apps/moon/assets/images/clinic-services/lgbtq-care.png"),
  monitoring: require("@src/apps/moon/assets/images/steps/screening.png"),
};

function TreatmentCard({
  service,
  label,
  onPress,
}: {
  service: ResolvedClinicService;
  label: string;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("ClinicTreatmentCard");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(label), "clinic_treatment_card");
        onPress?.();
      }}
      disabled={!onPress}
      accessibilityRole={onPress ? "button" : undefined}
      accessibilityLabel={onPress ? `${label}. Open fact sheet` : `${label}.`}
      accessibilityHint={
        onPress ? "Opens the fact sheet for this service" : undefined
      }
      style={({ pressed }) => [
        {
          width: "31%",
          minHeight: 122,
          borderRadius: 16,
          padding: 10,
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(255,255,255,0.32)",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.48)",
          overflow: "hidden",
        },
        pressed && { opacity: 0.65 },
      ]}
    >
      {onPress ? (
        <View
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            borderRadius: 10,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.58)",
          }}
        >
          <Info size={12} color={EMBER.colors.textMid} strokeWidth={2.2} />
        </View>
      ) : null}
      <Image
        source={SERVICE_ILLUSTRATIONS[service.key]}
        resizeMode="contain"
        accessibilityIgnoresInvertColors
        style={{ width: "100%", height: 72 }}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 16,
          fontWeight: "600",
          color: EMBER.colors.textMid,
          textAlign: "center",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

type DetailBlock =
  ContentBlock<"procedure"> | ContentBlock<"test"> | ContentBlock<"therapy">;

function getDetailBlock(
  idStr: string | undefined,
  procedures: ContentBlock<"procedure">[],
  tests: ContentBlock<"test">[],
  therapies: ContentBlock<"therapy">[],
): DetailBlock | null {
  if (!idStr) return null;
  const id = makeId(idStr);
  return (
    procedures.find((block) => block.id === id) ??
    tests.find((block) => block.id === id) ??
    therapies.find((block) => block.id === id) ??
    null
  );
}

function IllustratedClinicServicesCard({ org }: Props) {
  const t = useTranslations();
  const devOverrides = useMemo(() => getDevServiceOverrides(), []);
  const defaultSource =
    org._enrichment.sections.treatments?.source ??
    org._enrichment.sections.website?.source;
  const services = useMemo(
    () =>
      resolveClinicServices({
        treatments: org.treatments ?? [],
        patientGroups: org.patientGroups,
        hasOnSiteLab: org.outcomes?.hasOnSiteLab,
        capabilityOverrides: devOverrides ?? org.serviceCapabilities,
        defaultSource,
      }),
    [
      defaultSource,
      devOverrides,
      org.outcomes?.hasOnSiteLab,
      org.patientGroups,
      org.serviceCapabilities,
      org.treatments,
    ],
  );
  const procedures = useMemo(() => getBlocksByKind("procedure"), []);
  const tests = useMemo(() => getBlocksByKind("test"), []);
  const therapies = useMemo(() => getBlocksByKind("therapy"), []);
  const [selected, setSelected] = useState<DetailBlock | null>(null);
  const hasSpecificPreservationService = services.groups.some(
    (group) =>
      group.key === "fertility-preservation" &&
      group.items.some((service) => service.key !== "fertility-preservation"),
  );

  return (
    <View style={{ gap: 22 }}>
      {services.groups.map((group) => {
        const visibleItems = group.items.filter(
          (service) =>
            !(
              hasSpecificPreservationService &&
              group.key === "fertility-preservation" &&
              service.key === "fertility-preservation"
            ),
        );

        return (
          <View key={group.key} style={{ gap: 8 }}>
            <GroupLabel>
              {translateServiceText(t, "group", group.key, group.label)}
            </GroupLabel>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {visibleItems.map((service) => {
                const block = getDetailBlock(
                  SERVICE_CONTENT[service.key],
                  procedures,
                  tests,
                  therapies,
                );
                return (
                  <TreatmentCard
                    key={service.key}
                    service={service}
                    label={translateServiceText(
                      t,
                      "item",
                      service.key,
                      service.label,
                    )}
                    onPress={block ? () => setSelected(block) : undefined}
                  />
                );
              })}
            </View>
          </View>
        );
      })}

      {selected?.kind === "procedure" ? (
        <ProcedureDetailSheet
          item={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
      {selected?.kind === "test" ? (
        <TestDetailSheet item={selected} onClose={() => setSelected(null)} />
      ) : null}
      {selected?.kind === "therapy" ? (
        <TherapyDetailSheet item={selected} onClose={() => setSelected(null)} />
      ) : null}
    </View>
  );
}

/** Translate a category key from clinicCapabilities to a chrome label. Falls
 *  back to the english label from the resolver if the key isn't in the i18n
 *  table (so unknown categories still render). */
function translateCategoryLabel(
  t: TranslationFunction,
  categoryKey: string,
  fallback: string,
): string {
  const path = `access.capabilities.category.${categoryKey}`;
  const translated = t(path);
  // The i18n hook returns the path itself when missing; fall back in that case.
  return translated === path ? fallback : translated;
}

/** Translate a capability catalog item to a chrome label. Falls back to the
 *  english label from the resolver for items not in the i18n table. */
function translateCapabilityItemLabel(
  t: TranslationFunction,
  itemKey: string,
  fallback: string,
): string {
  const path = `access.capabilities.item.${itemKey}`;
  const translated = t(path);
  return translated === path ? fallback : translated;
}

function LegacyClinicCapabilitiesCard({ org }: Props) {
  const t = useTranslations();
  const { treatmentGroups, capabilityGroups } = resolveClinicCapabilities({
    treatments: org.treatments ?? [],
    hasOnSiteLab: org.outcomes?.hasOnSiteLab,
  });

  const procedures = useMemo(() => getBlocksByKind("procedure"), []);
  const tests = useMemo(() => getBlocksByKind("test"), []);
  const therapies = useMemo(() => getBlocksByKind("therapy"), []);
  const [selected, setSelected] = useState<
    | ContentBlock<"procedure">
    | ContentBlock<"test">
    | ContentBlock<"therapy">
    | null
  >(null);

  // Resolve a content id string to an authored procedure, test, or therapy block.
  const blockFor = useCallback(
    (idStr: string | undefined) => {
      if (!idStr) return null;
      const id = makeId(idStr);
      return (
        procedures.find((p) => p.id === id) ??
        tests.find((t) => t.id === id) ??
        therapies.find((t) => t.id === id) ??
        null
      );
    },
    [procedures, tests, therapies],
  );

  return (
    <View style={{ gap: 22 }}>
      {/* Treatments the clinic offers, grouped by category */}
      {treatmentGroups.length > 0 ? (
        treatmentGroups.map((group) => (
          <View key={group.category}>
            <GroupLabel>
              {translateCategoryLabel(t, group.category, group.label)}
            </GroupLabel>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              {group.treatments.map((name) => {
                const block = blockFor(TREATMENT_CONTENT[name]);
                return (
                  <TreatmentChip
                    key={name}
                    label={name}
                    onPress={block ? () => setSelected(block) : undefined}
                  />
                );
              })}
            </View>
          </View>
        ))
      ) : (
        <View>
          <GroupLabel>
            {translateCategoryLabel(t, "art", "Core treatments")}
          </GroupLabel>
          <NoInfo
            label={t("access.capabilities.no_info_label_confirmed_treatments")}
            t={t}
          />
        </View>
      )}

      {/* Curated capability groups. We only show confirmed (offered) items; if
          a group has none, we keep it but say we don't have the info, instead
          of a wall of grayed-out "not offered" pills. */}
      {capabilityGroups.map((group) => {
        const offered = group.items.filter((i) => i.status === "offered");
        const groupLabel = translateCategoryLabel(t, group.key, group.label);
        return (
          <View key={group.key}>
            <GroupLabel>{groupLabel}</GroupLabel>
            {offered.length > 0 ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {offered.map((item) => {
                  const block = blockFor(CAPABILITY_CONTENT[item.key]);
                  return (
                    <CapabilityChip
                      key={item.key}
                      label={translateCapabilityItemLabel(
                        t,
                        item.key,
                        item.label,
                      )}
                      evidence={item.evidence}
                      onPress={block ? () => setSelected(block) : undefined}
                    />
                  );
                })}
              </View>
            ) : (
              <NoInfo label={groupLabel} t={t} />
            )}
          </View>
        );
      })}

      {selected?.kind === "procedure" ? (
        <ProcedureDetailSheet
          item={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
      {selected?.kind === "test" ? (
        <TestDetailSheet item={selected} onClose={() => setSelected(null)} />
      ) : null}
      {selected?.kind === "therapy" ? (
        <TherapyDetailSheet item={selected} onClose={() => setSelected(null)} />
      ) : null}
    </View>
  );
}

export default function ClinicCapabilitiesCard({ org }: Props) {
  const country = org.address.country.toUpperCase();
  const usesIllustratedServices = country === "US" || country === "FR";
  return usesIllustratedServices ? (
    <IllustratedClinicServicesCard org={org} />
  ) : (
    <LegacyClinicCapabilitiesCard org={org} />
  );
}
